import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import MenuBar from "../../layouts/MenuBar";
import Layout from "../../layouts/Layout";
import "./ExternalLinks.scss";
import * as PR from "../../prime-react/index";
import { useToast } from "../../context/ToastContext";
import { getCategoryListAPI, getFileDetails } from "../../services/api";
import ImageDisplay from "../ImageDisplay";
import AddCategory from "./Category/AddCategory";
import EditCategory from "./Category/EditCategory";
import { processCategories } from "../../utils/reuse";

const ExternalLinks = () => {
    const dispatch = useDispatch();
    const sessionData = useSelector(state => state.auth.sessionData);
    const headers = useMemo(() => ({ sessionid: sessionData.sessionId }), [sessionData.sessionId]);
    const role = useSelector(state => state.auth.role);
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);

    const [categories, setCategoryList] = useState([]);

    const [addCategoryDisplay, setAddCategoryDisplay] = useState(false);
    const [editCategoryDisplay, setEditCategoryDisplay] = useState(false);

    const [selectedCategory, setSelectedCategory] = useState(null);

    const pageCount = 50;
    const totalPages = Math.ceil(totalRecords / pageCount);
    const hasNextPage = currentPage < totalPages;
    const skipNextFetchRef = useRef(false);

    const getCategories = useCallback((page = 1) => {
        setLoading(true);
    
        const params = {
            offset: (page - 1) * pageCount,
            limit: pageCount
        };
    
        getCategoryListAPI(params, headers, dispatch, (response) => {
            if (response.result === 'SUCCESS') {
                const data = response.data?.categories || [];
                const total = response.data?.total || 0;
                setTotalRecords(total);
                const processedData = processCategories(data);
                setCategoryList(processedData);
            } else {
                setCategoryList([]);
                setTotalRecords(0);
                const error = response.error;
                showToast(error.severity, error?.errorMsg || error?.summary);
            }
            setLoading(false);
        });
    }, [dispatch, headers, pageCount, showToast]);

    useEffect(() => {
        if (skipNextFetchRef.current) {
            skipNextFetchRef.current = false; // reset the flag
            return; // skip API call
        }
        getCategories(currentPage);
    }, [currentPage, getCategories]);

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    const fetchFileDetails = (key, fileName) => {
        const convertBlobToPDFAndDownload = async (pdfData, fileName) => {
            try {
                const url = URL.createObjectURL(pdfData);

                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName || 'file.pdf');

                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            } catch (error) {
                console.error('Error converting base64 to PDF:', error);
            }
        };

        getFileDetails(key, headers, dispatch, response => {
            if (response.result === 'SUCCESS') {
                const fileData = response.data;
                convertBlobToPDFAndDownload(fileData, fileName);
            } else {
                const error = response?.error;
                showToast(error.severity, error?.errorMsg || error?.summary);
            }
        });
    };

    return (
        <>
            <Layout backBtn={"home"} backBtnLabel="Home">
                <MenuBar />
                <section className={`external-links-tab ${role === 'member' || role === 'managedmember' ? 'member-links' : 'reg-links'}`}>
                    <div className="flex align-items-center justify-content-between">
                        <div className="m-0">
                            <h1>External Links</h1>
                        </div>
                        {role === 'analyst' && <PR.Button label="Add Category" icon="pi pi-plus-circle" className="links-categories-button" onClick={() => setAddCategoryDisplay(true)} />}
                    </div>
                    <div className="grid mt-2">
                        <div className="col-12">
                            <div className="links-left-section relative">
                                {/* categories */}
                                {loading ? (
                                    <div className="loading">
                                        <i className="pi pi-spin pi-spinner"></i>
                                    </div>
                                ) : categories && categories.length > 0 ? (
                                    categories?.map(((item, index) => {
                                        return (
                                            <div className="link-container" key={index}>
                                                <div className="flex align-items-start justify-content-between gap-3">
                                                    <div className="flex gap-3 align-items-start">
                                                        <div className="icon">
                                                            <div className='sports-icon'>
                                                                <ImageDisplay profileImage={item?.category.icon} type="category" />
                                                            </div>
                                                        </div>
                                                        <div className="link-info">
                                                            <h4>{item?.category.name}</h4>
                                                            <div className="flex flex-column gap-2">
                                                                {item?.links?.map((link, index) => (
                                                                    link.url ?
                                                                        <Link key={index} to={link?.url} className="links" target="_blank">
                                                                            <span className="link-description">{link?.description}</span>
                                                                            <i className="pi pi-arrow-up-right"></i>
                                                                        </Link>
                                                                        :
                                                                        <Link key={index} onClick={() => fetchFileDetails(link?.file.key, link?.file.name)} className="links">
                                                                            <span className="link-description">{link?.description}</span>
                                                                            <i className="pi pi-download"></i>
                                                                        </Link>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {role === 'analyst' && 
                                                    <div className="flex gap-2">
                                                        <PR.Button label="Edit" className="links-categories-button" 
                                                        onClick={() => { setEditCategoryDisplay(true); setSelectedCategory(item); }} />
                                                        {/* <PR.Button label="Delete" className="links-categories-button delete" 
                                                        onClick={() => { setEditCategoryDisplay(true); setSelectedCategory(item); }} /> */}
                                                    </div>
                                                    }
                                                </div>
                                            </div> 
                                        )
                                    }))
                                ) : (
                                    <div className="flex justify-content-center">
                                        <p className="no-data m-0 text-bluegray-500">
                                            No categories found
                                        </p>
                                    </div>
                                )
                                }
                            </div>
                            {/* Pagination Section */}
                            {categories.length > 0 && <div className="pagination">
                                <div className="grid grid-nogutter align-items-center justify-content-between">
                                    <span>Page {currentPage}</span>
                                    <ul className="flex list-none align-items-center gap-3">
                                        <li className={currentPage === 1 ? "disabled" : ""} onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}>
                                            Previous
                                        </li>
                                        <li className={!hasNextPage ? "disabled" : ""} onClick={() => hasNextPage && handlePageChange(currentPage + 1)}>
                                            Next
                                        </li>
                                    </ul>
                                </div>
                            </div>}
                        </div>
                    </div>
                </section>

                {/* Add Category */}
                <AddCategory addCategoryDisplay={addCategoryDisplay} setAddCategoryDisplay={setAddCategoryDisplay} setCategoryList={setCategoryList} setTotalRecords={setTotalRecords} resetPageWithoutFetch={() => {
                    skipNextFetchRef.current = true;
                    setCurrentPage(1);
                }} />

                {/* Edit Dialog */}
                <EditCategory editCategoryDisplay={editCategoryDisplay} setEditCategoryDisplay={setEditCategoryDisplay} getCategories={getCategories} selectedCategory={selectedCategory} />
            </Layout>
        </>
    );
};

export default ExternalLinks;